/**
 * Solitaire Engine — Core Klondike Solitaire Game Logic
 *
 * Handles deck creation, shuffling, dealing, card movement,
 * validation, undo, and auto-complete.
 *
 * Rules:
 * - 7 tableau columns (1-7 cards, top card face-up)
 * - 4 foundation piles (build Ace→King per suit)
 * - Tableau: build down in alternating colors
 * - Stock draws to waste (draw 1 or draw 3)
 * - When stock empty, flip waste back to stock
 * - Win when all 4 foundations are Ace→King
 */

import {
  Card,
  Suit,
  CardSource,
  SolitaireGameState,
  SolitaireMove,
  SUITS,
  getCardColor,
} from './types';

export class SolitaireEngine {
  private gameState: SolitaireGameState | null = null;

  // =========================================================================
  // Public API
  // =========================================================================

  /** Create a new game: shuffle deck, deal tableau, set up stock. */
  initializeGame(drawMode: 1 | 3): SolitaireGameState {
    const deck = this.createDeck();
    this.shuffleDeck(deck);

    // Deal tableau: column i gets i+1 cards, only top card face-up
    const tableau: Card[][] = [];
    let deckIndex = 0;
    for (let col = 0; col < 7; col++) {
      const column: Card[] = [];
      for (let row = 0; row <= col; row++) {
        const card = { ...deck[deckIndex++] };
        card.faceUp = row === col; // Only top card face-up
        column.push(card);
      }
      tableau.push(column);
    }

    // Remaining cards go to stock (face-down)
    const stock: Card[] = [];
    while (deckIndex < deck.length) {
      const card = { ...deck[deckIndex++] };
      card.faceUp = false;
      stock.push(card);
    }

    this.gameState = {
      stock,
      waste: [],
      foundations: [[], [], [], []],
      tableau,
      moveHistory: [],
      timer: 0,
      moveCount: 0,
      gameStatus: 'playing',
      drawMode,
      gameId: this.generateId(),
      startedAt: Date.now(),
    };

    return this.getGameState();
  }

  /** Get a deep copy of the current game state. */
  getGameState(): SolitaireGameState {
    if (!this.gameState) {
      throw new Error('Game not initialized');
    }
    return JSON.parse(JSON.stringify(this.gameState));
  }

  /** Draw card(s) from stock to waste. */
  drawFromStock(): SolitaireGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');

    if (this.gameState.stock.length === 0) {
      // Reset: flip waste back to stock
      if (this.gameState.waste.length === 0) return this.getGameState();

      const move: SolitaireMove = {
        from: { type: 'waste', index: 0, cardIndex: 0 },
        to: { type: 'stock', index: 0, cardIndex: 0 },
        cardCount: this.gameState.waste.length,
        flippedCard: false,
        wasDraw: false,
        drawnCount: 0,
        wasStockReset: true,
        timestamp: Date.now(),
      };

      // Move all waste cards back to stock (reversed, face-down)
      const wasteCards = this.gameState.waste.splice(0);
      wasteCards.reverse();
      for (const card of wasteCards) {
        card.faceUp = false;
      }
      this.gameState.stock = wasteCards;
      this.gameState.moveHistory.push(move);
      this.gameState.moveCount++;

      return this.getGameState();
    }

    // Draw cards from stock to waste
    const drawCount = Math.min(this.gameState.drawMode, this.gameState.stock.length);
    const drawnCards: Card[] = [];

    for (let i = 0; i < drawCount; i++) {
      const card = this.gameState.stock.pop()!;
      card.faceUp = true;
      drawnCards.push(card);
    }

    // Push drawn cards to waste (last drawn is on top)
    this.gameState.waste.push(...drawnCards);

    const move: SolitaireMove = {
      from: { type: 'stock', index: 0, cardIndex: 0 },
      to: { type: 'waste', index: 0, cardIndex: 0 },
      cardCount: drawCount,
      flippedCard: false,
      wasDraw: true,
      drawnCount: drawCount,
      wasStockReset: false,
      timestamp: Date.now(),
    };

    this.gameState.moveHistory.push(move);
    this.gameState.moveCount++;

    return this.getGameState();
  }

  /** Move a card to the appropriate foundation pile. */
  moveToFoundation(source: CardSource): SolitaireGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');

    const card = this.getTopCardFromSource(source);
    if (!card) throw new Error('No card at source');

    const foundationIndex = this.findFoundationForCard(card);
    if (foundationIndex === -1) throw new Error('Cannot move card to foundation');

    // Remove card from source
    this.removeCardFromSource(source);

    // Add to foundation
    this.gameState.foundations[foundationIndex].push(card);

    // Flip newly exposed card
    const flipped = this.flipTopCard(source);

    const move: SolitaireMove = {
      from: source,
      to: { type: 'foundation', index: foundationIndex, cardIndex: this.gameState.foundations[foundationIndex].length - 1 },
      cardCount: 1,
      flippedCard: flipped,
      wasDraw: false,
      drawnCount: 0,
      wasStockReset: false,
      timestamp: Date.now(),
    };

    this.gameState.moveHistory.push(move);
    this.gameState.moveCount++;

    // Check win
    if (this.isGameWon()) {
      this.gameState.gameStatus = 'completed';
    }

    return this.getGameState();
  }

  /** Move card(s) to a tableau column. */
  moveToTableau(source: CardSource, targetColumn: number): SolitaireGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');
    if (targetColumn < 0 || targetColumn > 6) throw new Error('Invalid target column');

    const targetPile = this.gameState.tableau[targetColumn];

    if (source.type === 'tableau') {
      // Moving a sequence from tableau
      const sourcePile = this.gameState.tableau[source.index];
      const cardIndex = source.cardIndex;
      const card = sourcePile[cardIndex];

      if (!card || !card.faceUp) throw new Error('Invalid card selection');

      // Validate the move
      if (!this.canPlaceOnTableau(card, targetPile)) {
        throw new Error('Invalid tableau move');
      }

      // Move the sequence
      const cardsToMove = sourcePile.splice(cardIndex);
      targetPile.push(...cardsToMove);

      // Flip newly exposed card
      const flipped = this.flipTopCard({ type: 'tableau', index: source.index, cardIndex: 0 });

      const move: SolitaireMove = {
        from: source,
        to: { type: 'tableau', index: targetColumn, cardIndex: targetPile.length - cardsToMove.length },
        cardCount: cardsToMove.length,
        flippedCard: flipped,
        wasDraw: false,
        drawnCount: 0,
        wasStockReset: false,
        timestamp: Date.now(),
      };

      this.gameState.moveHistory.push(move);
      this.gameState.moveCount++;
    } else {
      // Moving single card from waste or foundation
      const card = this.getTopCardFromSource(source);
      if (!card) throw new Error('No card at source');

      if (!this.canPlaceOnTableau(card, targetPile)) {
        throw new Error('Invalid tableau move');
      }

      this.removeCardFromSource(source);
      targetPile.push(card);

      const move: SolitaireMove = {
        from: source,
        to: { type: 'tableau', index: targetColumn, cardIndex: targetPile.length - 1 },
        cardCount: 1,
        flippedCard: false,
        wasDraw: false,
        drawnCount: 0,
        wasStockReset: false,
        timestamp: Date.now(),
      };

      this.gameState.moveHistory.push(move);
      this.gameState.moveCount++;
    }

    return this.getGameState();
  }

  /** Check if a card can be moved to any foundation. Returns foundation index or -1. */
  findFoundationForCard(card: Card): number {
    if (!this.gameState) return -1;

    for (let i = 0; i < 4; i++) {
      const foundation = this.gameState.foundations[i];
      if (foundation.length === 0) {
        // Empty foundation: only Ace
        if (card.rank === 1) return i;
      } else {
        const topCard = foundation[foundation.length - 1];
        if (topCard.suit === card.suit && topCard.rank === card.rank - 1) {
          return i;
        }
      }
    }
    return -1;
  }

  /** Check if auto-complete is possible (all cards face-up). */
  canAutoComplete(): boolean {
    if (!this.gameState) return false;
    if (this.gameState.gameStatus !== 'playing') return false;

    // Stock must be empty
    if (this.gameState.stock.length > 0) return false;

    // Waste must be empty
    if (this.gameState.waste.length > 0) return false;

    // All tableau cards must be face-up
    for (const column of this.gameState.tableau) {
      for (const card of column) {
        if (!card.faceUp) return false;
      }
    }

    return true;
  }

  /** Auto-complete: move all remaining cards to foundations. */
  autoComplete(): SolitaireGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (!this.canAutoComplete()) throw new Error('Cannot auto-complete');

    let moved = true;
    while (moved) {
      moved = false;

      // Try to move from each tableau column
      for (let col = 0; col < 7; col++) {
        const column = this.gameState.tableau[col];
        if (column.length === 0) continue;

        const topCard = column[column.length - 1];
        const foundationIndex = this.findFoundationForCard(topCard);

        if (foundationIndex !== -1) {
          column.pop();
          this.gameState.foundations[foundationIndex].push(topCard);
          this.gameState.moveCount++;
          moved = true;
        }
      }
    }

    if (this.isGameWon()) {
      this.gameState.gameStatus = 'completed';
    }

    return this.getGameState();
  }

  /** Undo the last move. */
  undo(): SolitaireGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'playing') throw new Error('Game is not in progress');
    if (this.gameState.moveHistory.length === 0) return this.getGameState();

    const lastMove = this.gameState.moveHistory.pop()!;

    if (lastMove.wasStockReset) {
      // Undo stock reset: move stock back to waste (reversed)
      const stockCards = this.gameState.stock.splice(0);
      stockCards.reverse();
      for (const card of stockCards) {
        card.faceUp = true;
      }
      this.gameState.waste = stockCards;
    } else if (lastMove.wasDraw) {
      // Undo draw: move cards from waste back to stock
      const drawnCards = this.gameState.waste.splice(
        this.gameState.waste.length - lastMove.drawnCount
      );
      drawnCards.reverse();
      for (const card of drawnCards) {
        card.faceUp = false;
      }
      this.gameState.stock.push(...drawnCards);
    } else {
      // Undo a card move
      // Un-flip the card that was flipped
      if (lastMove.flippedCard && lastMove.from.type === 'tableau') {
        const sourcePile = this.gameState.tableau[lastMove.from.index];
        if (sourcePile.length > 0) {
          sourcePile[sourcePile.length - 1].faceUp = false;
        }
      }

      // Move cards back
      const toPile = this.getPile(lastMove.to);
      const cardsToReturn = toPile.splice(toPile.length - lastMove.cardCount);

      const fromPile = this.getPile(lastMove.from);
      fromPile.push(...cardsToReturn);
    }

    this.gameState.moveCount = Math.max(0, this.gameState.moveCount - 1);

    return this.getGameState();
  }

  /** Check if all 4 foundations are complete (Ace through King). */
  isGameWon(): boolean {
    if (!this.gameState) return false;
    return this.gameState.foundations.every((f) => f.length === 13);
  }

  /** Update the timer value. */
  updateTimer(seconds: number): void {
    if (this.gameState) {
      this.gameState.timer = seconds;
    }
  }

  /** Check if a card can be moved to a specific foundation. */
  canMoveToFoundation(card: Card): boolean {
    return this.findFoundationForCard(card) !== -1;
  }

  /** Check if a card/sequence can be placed on a tableau column. */
  canPlaceOnTableau(card: Card, targetPile: Card[]): boolean {
    if (targetPile.length === 0) {
      // Empty column: only King
      return card.rank === 13;
    }

    const topCard = targetPile[targetPile.length - 1];
    if (!topCard.faceUp) return false;

    // Must be alternating color and one rank lower
    return (
      getCardColor(card) !== getCardColor(topCard) &&
      card.rank === topCard.rank - 1
    );
  }

  /** Check if there are any valid moves available. */
  hasValidMoves(): boolean {
    if (!this.gameState) return false;

    // Can draw from stock
    if (this.gameState.stock.length > 0) return true;

    // Can reset stock from waste
    if (this.gameState.waste.length > 0 && this.gameState.stock.length === 0) return true;

    // Check waste top card
    if (this.gameState.waste.length > 0) {
      const wasteTop = this.gameState.waste[this.gameState.waste.length - 1];
      if (this.findFoundationForCard(wasteTop) !== -1) return true;
      for (const col of this.gameState.tableau) {
        if (this.canPlaceOnTableau(wasteTop, col)) return true;
      }
    }

    // Check tableau moves
    for (let i = 0; i < 7; i++) {
      const col = this.gameState.tableau[i];
      if (col.length === 0) continue;

      // Check top card to foundation
      const topCard = col[col.length - 1];
      if (this.findFoundationForCard(topCard) !== -1) return true;

      // Check face-up sequences to other tableau columns
      for (let cardIdx = 0; cardIdx < col.length; cardIdx++) {
        if (!col[cardIdx].faceUp) continue;
        for (let j = 0; j < 7; j++) {
          if (i === j) continue;
          if (this.canPlaceOnTableau(col[cardIdx], this.gameState.tableau[j])) return true;
        }
      }
    }

    return false;
  }

  /** Serialize game state for persistence. */
  serialize(): string {
    return JSON.stringify(this.gameState);
  }

  /** Deserialize and restore game state. */
  deserialize(data: string): SolitaireGameState {
    this.gameState = JSON.parse(data);
    return this.getGameState();
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  /** Create a standard 52-card deck. */
  private createDeck(): Card[] {
    const deck: Card[] = [];
    for (const suit of SUITS) {
      for (let rank = 1; rank <= 13; rank++) {
        deck.push({
          id: `${suit}-${rank}`,
          suit,
          rank,
          faceUp: false,
        });
      }
    }
    return deck;
  }

  /** Fisher-Yates shuffle. */
  private shuffleDeck(deck: Card[]): void {
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
  }

  /** Get the top card from a source location. */
  private getTopCardFromSource(source: CardSource): Card | null {
    if (!this.gameState) return null;

    switch (source.type) {
      case 'waste': {
        const waste = this.gameState.waste;
        return waste.length > 0 ? waste[waste.length - 1] : null;
      }
      case 'tableau': {
        const col = this.gameState.tableau[source.index];
        return col.length > 0 ? col[col.length - 1] : null;
      }
      case 'foundation': {
        const found = this.gameState.foundations[source.index];
        return found.length > 0 ? found[found.length - 1] : null;
      }
      default:
        return null;
    }
  }

  /** Remove the top card from a source location. */
  private removeCardFromSource(source: CardSource): void {
    if (!this.gameState) return;

    switch (source.type) {
      case 'waste':
        this.gameState.waste.pop();
        break;
      case 'tableau':
        this.gameState.tableau[source.index].pop();
        break;
      case 'foundation':
        this.gameState.foundations[source.index].pop();
        break;
    }
  }

  /** Flip the top card of a tableau column face-up if it's face-down. Returns true if flipped. */
  private flipTopCard(source: CardSource): boolean {
    if (!this.gameState || source.type !== 'tableau') return false;

    const col = this.gameState.tableau[source.index];
    if (col.length > 0 && !col[col.length - 1].faceUp) {
      col[col.length - 1].faceUp = true;
      return true;
    }
    return false;
  }

  /** Get the pile array for a given source. */
  private getPile(source: CardSource): Card[] {
    if (!this.gameState) return [];

    switch (source.type) {
      case 'stock':
        return this.gameState.stock;
      case 'waste':
        return this.gameState.waste;
      case 'foundation':
        return this.gameState.foundations[source.index];
      case 'tableau':
        return this.gameState.tableau[source.index];
      default:
        return [];
    }
  }

  private generateId(): string {
    return `solitaire_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }
}
